/**
 * Touch controls (PRD §5 supports keyboard and gamepad; phones need a third
 * scheme). A floating analog stick on the left half — it appears wherever the
 * thumb lands rather than at a fixed spot, which is what makes a virtual stick
 * usable — and a cluster of action buttons on the right.
 *
 * Layout is computed in CSS pixels and drawn by the HUD, so the two never
 * disagree about where a button is.
 */

export type TouchButtonId =
  | 'fire' | 'torpedo' | 'sonar' | 'light' | 'nitro' | 'charge' | 'dock' | 'grab'
  | 'menu' | 'view'
  /**
   * Collapses the ore hold. Contextual — hidden until there is ore to hide,
   * because a button that shrinks an empty readout only teaches confusion.
   */
  | 'hold';

export interface TouchButton {
  id: TouchButtonId;
  label: string;
  /** Centre and radius in CSS pixels. */
  x: number;
  y: number;
  r: number;
  pressed: boolean;
  /** Eases the press highlight. */
  glow: number;
  /**
   * Contextual buttons are hidden until they can do something — a depth-charge
   * key the player has not bought yet is a button that only teaches confusion.
   */
  visible: boolean;
  /** Accent hue for the HUD; nitro and dock are deliberately not cyan. */
  accent?: string;
  /**
   * This button is also a one-axis stick: it keeps firing while held AND
   * reports how far the thumb has slid up or down it.
   *
   * A separate stick somewhere else on the glass would have been the tidier
   * data model and the worse control. A phone has two thumbs, both already
   * committed — one to the movement stick, one to this cluster — and the gun
   * has to be aimable while it is firing, which rules out anything the firing
   * thumb has to leave to reach.
   */
  stick?: boolean;
  /**
   * Two axes rather than one, and a tap that means something on its own.
   *
   * The view control is not a weapon: it has no held state worth reporting, so
   * the press that a `stick` button spends on firing is free here and spends
   * itself on RESET instead. Drag to look, tap to put it back — which is the
   * whole control, and the reason it is one button and not three.
   */
  stick2?: boolean;
  /** Thumb deflection, -1..1. `knobY` is UP POSITIVE; `knobX` right positive. */
  knobX: number;
  knobY: number;
}

export interface TouchLayout {
  buttons: TouchButton[];
  /** Where the stick is currently anchored, if a thumb is down. */
  stickX: number;
  stickY: number;
  stickR: number;
  /** Thumb position relative to the anchor, normalised to the stick radius. */
  knobX: number;
  knobY: number;
  active: boolean;
  /** Resting position drawn as a hint before first touch. */
  restX: number;
  restY: number;
}

const clamp1 = (v: number) => (v < -1 ? -1 : v > 1 ? 1 : v);

export type TouchMode = 'auto' | 'on' | 'off';

const STORE_KEY = 'abyss.touch';

export function loadTouchMode(): TouchMode {
  const q = new URLSearchParams(location.search).get('touch');
  if (q === 'on' || q === 'off' || q === 'auto') return q;
  try {
    const v = localStorage.getItem(STORE_KEY);
    if (v === 'on' || v === 'off' || v === 'auto') return v;
  } catch {
    // Private mode or blocked storage — fall through to the default.
  }
  return 'auto';
}

export function saveTouchMode(m: TouchMode) {
  try {
    localStorage.setItem(STORE_KEY, m);
  } catch {
    // Not being able to remember the choice is not worth failing over.
  }
}

/** True on hardware that can actually produce touches. */
export function hasTouchHardware(): boolean {
  return (navigator.maxTouchPoints ?? 0) > 0;
}

export class TouchInput {
  mode: TouchMode = 'auto';
  /** Set by a real touch; cleared when the player goes back to keys or a pad. */
  private touched = false;
  moveX = 0;
  moveY = 0;
  /** Gun elevation demand, -1..1, up positive. Non-zero only while held. */
  gunY = 0;
  /** View stick: x zooms, y tilts. -1..1, and only while held. */
  viewX = 0;
  viewY = 0;
  private viewTapped = false;

  /**
   * A tap on the view control, read once. A DRAG IS NOT A TAP: the pointer has
   * to have gone essentially nowhere, or every look-around would end by
   * throwing the view back to default the moment the thumb came off.
   */
  consumeViewTap(): boolean {
    const v = this.viewTapped;
    this.viewTapped = false;
    return v;
  }

  /**
   * In auto mode the controls only exist once a finger has actually been used,
   * which is what keeps them off a desktop that merely reports touch points.
   */
  get enabled(): boolean {
    if (this.mode === 'off') return false;
    if (this.mode === 'on') return true;
    return this.touched;
  }

  readonly layout: TouchLayout = {
    buttons: [
      { id: 'fire', label: 'FIRE', x: 0, y: 0, r: 40, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: true, stick: true },
      { id: 'torpedo', label: 'TORP', x: 0, y: 0, r: 34, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: true },
      { id: 'nitro', label: 'NITRO', x: 0, y: 0, r: 34, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: true, accent: '#ff6ad5' },
      { id: 'sonar', label: 'SONAR', x: 0, y: 0, r: 30, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: true },
      { id: 'light', label: 'LAMP', x: 0, y: 0, r: 26, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: true },
      { id: 'charge', label: 'DEPTH', x: 0, y: 0, r: 26, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: false, accent: '#ff9a4a' },
      { id: 'dock', label: 'DOCK', x: 0, y: 0, r: 34, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: false, accent: '#ffb454' },
      { id: 'grab', label: 'GRAB', x: 0, y: 0, r: 30, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: false, accent: '#8fd9ff' },
      // On a phone there was NO WAY BACK TO THE MENU once a dive started —
      // no Escape key, and every other control is a thumb cluster in the
      // bottom corner. Top-left, away from both thumbs, so it cannot be hit
      // by accident while flying.
      { id: 'menu', label: 'MENU', x: 0, y: 0, r: 26, pressed: false, glow: 0, knobX: 0, knobY: 0, visible: true },
      // Right edge, half way down — the mirror of MENU, and the only other
      // place on a phone screen that neither thumb cluster wants. Deliberately
      // NOT in the action cluster: this is the one control here that is never
      // urgent, and putting it beside FIRE would mean hitting it in a fight.
      {
        id: 'view', label: 'VIEW', x: 0, y: 0, r: 26, pressed: false, glow: 0,
        knobX: 0, knobY: 0, visible: true, stick2: true, accent: '#9fb4c8',
      },
      // Top-right, beside the row it collapses. Small, because it is a toggle
      // nobody presses in a fight, and the only thing up there competing for
      // the thumb is nothing at all — both clusters are at the bottom.
      {
        id: 'hold', label: 'HOLD', x: 0, y: 0, r: 22, pressed: false, glow: 0,
        knobX: 0, knobY: 0, visible: false, accent: '#bfe9ff',
      },
    ],
    stickX: 0, stickY: 0, stickR: 62,
    knobX: 0, knobY: 0, active: false,
    restX: 0, restY: 0,
  };

  private stickPointer: number | null = null;
  private buttonPointer = new Map<number, TouchButton>();
  /** Where a stick-button's thumb first landed, so its travel is relative. */
  private buttonAnchor = new Map<number, number>();
  private buttonAnchorX = new Map<number, number>();
  /** Furthest the thumb has been from that anchor — a tap has to stay near 0. */
  private buttonTravel = new Map<number, number>();
  private w = 1;

  private el: HTMLElement;

  constructor(target: HTMLElement) {
    this.el = target;
    const opts = { passive: false } as const;
    target.addEventListener('pointerdown', (e) => this.down(e), opts);
    target.addEventListener('pointermove', (e) => this.move(e), opts);
    target.addEventListener('pointerup', (e) => this.up(e), opts);
    target.addEventListener('pointercancel', (e) => this.up(e), opts);
    // A pointer that escapes the element still has to release the stick.
    window.addEventListener('pointerup', (e) => this.up(e));
    window.addEventListener('pointercancel', (e) => this.up(e));
    target.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  resize(w: number, h: number, safeBottom = 0, safeRight = 0, safeLeft = 0, safeTop = 0) {
    this.w = w;
    const L = this.layout;
    const unit = Math.max(38, Math.min(w, h) * 0.085);

    L.stickR = unit * 1.55;
    L.restX = safeLeft + unit * 2.3;
    L.restY = h - safeBottom - unit * 2.3;
    if (!L.active) {
      L.stickX = L.restX;
      L.stickY = L.restY;
    }

    const bx = w - safeRight - unit * 1.9;
    const by = h - safeBottom - unit * 1.9;
    const at = (id: TouchButtonId, r: number, dx: number, dy: number) => {
      const b = this.layout.buttons.find((q) => q.id === id)!;
      b.r = unit * r;
      b.x = bx + unit * dx;
      b.y = by + unit * dy;
    };
    at('fire', 1.05, 0, 0);
    at('torpedo', 0.84, -1.95, -0.45);
    at('nitro', 0.82, -0.3, -2.1);
    at('sonar', 0.66, -2.35, -2.1);
    at('light', 0.56, -3.55, -1.05);
    at('charge', 0.62, -1.35, -3.4);
    // Above LAMP, on the outside of the cluster: the grapple is aimed with the
    // stick, so the thumb that fires it should not be over the fire button.
    at('grab', 0.62, -4.15, -2.5);

    // Left edge, half way down. Deliberately not a corner: the readout owns
    // the top — and owns it harder in portrait, where the whole HUD moves up
    // to keep the bottom clear for thumbs — the stick owns the bottom left and
    // the cluster the bottom right. The middle of the left edge is the one
    // place on a phone screen that nothing else wants.
    const menu = this.layout.buttons.find((q) => q.id === 'menu')!;
    menu.r = unit * 0.52;
    menu.x = safeLeft + unit * 0.9;
    menu.y = h * 0.5;

    // The hold toggle, tucked under the ordnance column on the right where
    // nothing else lives. Left of the edge by its own radius so the tap target
    // is fully on screen inside the safe area.
    const hold = this.layout.buttons.find((q) => q.id === 'hold')!;
    hold.r = unit * 0.44;
    hold.x = w - safeRight - unit * 0.9;
    hold.y = h * 0.30;

    // A shade larger than MENU: it is dragged rather than tapped, and a stick
    // you can slide off is worse than a button you can miss.
    const view = this.layout.buttons.find((q) => q.id === 'view')!;
    view.r = unit * 0.62;
    view.x = w - safeRight - unit * 1.0;
    view.y = h * 0.5;
    void safeTop;

    // Docking is a rare, deliberate action, so it gets the middle of the screen
    // rather than a corner — and it only exists while a station is in reach.
    const dock = this.layout.buttons.find((q) => q.id === 'dock')!;
    dock.r = unit * 0.9;
    dock.x = w * 0.5;
    dock.y = h - safeBottom - unit * 1.2;
  }

  private hitButton(x: number, y: number): TouchButton | null {
    let best: TouchButton | null = null;
    let bestD = Infinity;
    for (const b of this.layout.buttons) {
      if (!b.visible) continue;
      // Generous hit radius — thumbs are imprecise and the cost of a miss is death.
      const d = Math.hypot(x - b.x, y - b.y);
      if (d <= b.r * 1.45 && d < bestD) { bestD = d; best = b; }
    }
    return best;
  }

  /** Show or hide a contextual button. */
  setVisible(id: TouchButtonId, on: boolean) {
    const b = this.layout.buttons.find((q) => q.id === id);
    if (!b) return;
    if (b.visible === on) return;
    b.visible = on;
    if (!on) b.pressed = false;
  }

  /** Drop every held control — used when a menu takes over the screen. */
  releaseAll() {
    for (const b of this.layout.buttons) { b.pressed = false; b.knobX = 0; b.knobY = 0; }
    this.gunY = 0;
    this.viewX = 0;
    this.viewY = 0;
    // A pending tap dies with the controls. Otherwise a press that a menu
    // interrupted would reset the camera on the frame the menu closed.
    this.viewTapped = false;
    this.buttonPointer.clear();
    this.buttonAnchor.clear();
    this.buttonAnchorX.clear();
    this.buttonTravel.clear();
    this.stickPointer = null;
    this.layout.active = false;
    this.layout.knobX = 0;
    this.layout.knobY = 0;
    this.layout.stickX = this.layout.restX;
    this.layout.stickY = this.layout.restY;
    this.moveX = 0;
    this.moveY = 0;
  }

  /**
   * While a menu is up the whole screen is a list, not a stick: taps are queued
   * raw for the HUD to hit-test against whatever it just drew. A mouse counts
   * here — menus are the one place a desktop pointer is a legitimate input.
   */
  menuMode = false;
  private taps: { x: number; y: number }[] = [];

  /** Taps recorded since the last call. Cleared by reading. */
  consumeTaps(): { x: number; y: number }[] {
    if (!this.taps.length) return [];
    const out = this.taps;
    this.taps = [];
    return out;
  }

  private down(e: PointerEvent) {
    if (this.mode === 'off') return;
    if (this.menuMode) {
      this.taps.push({ x: e.clientX, y: e.clientY });
      if (e.pointerType !== 'mouse') this.touched = true;
      return;
    }
    // A mouse must never bring the on-screen stick up.
    if (e.pointerType === 'mouse') return;
    this.touched = true;
    e.preventDefault();
    const x = e.clientX, y = e.clientY;

    const btn = this.hitButton(x, y);
    if (btn) {
      btn.pressed = true;
      this.buttonPointer.set(e.pointerId, btn);
      // Anchored where the thumb LANDED, not at the button's centre, for the
      // same reason the movement stick is: the hit radius is 1.45x the drawn
      // circle, so a press that lands on the rim would otherwise command a
      // third of full elevation before the thumb has moved at all.
      if (btn.stick || btn.stick2) {
        this.buttonAnchor.set(e.pointerId, y);
        this.buttonAnchorX.set(e.pointerId, x);
        this.buttonTravel.set(e.pointerId, 0);
        btn.knobX = 0;
        btn.knobY = 0;
      }
      return;
    }
    if (this.stickPointer === null && x < this.w * 0.55) {
      this.stickPointer = e.pointerId;
      // Capture explicitly. Without it a drag that leaves the element stops
      // delivering moves, and the stick silently sticks at its last value.
      try { this.el.setPointerCapture(e.pointerId); } catch { /* an optimisation, not a requirement */ }
      const L = this.layout;
      L.active = true;
      // Anchor where the thumb landed, never at the ring's centre: touching
      // down near the rim would otherwise command full deflection instantly.
      L.stickX = x;
      L.stickY = y;
      L.knobX = 0;
      L.knobY = 0;
      this.moveX = 0;
      this.moveY = 0;
    }
  }

  private move(e: PointerEvent) {
    // A stick BUTTON steers while it fires. The pointer stays bound to the
    // button it landed on however far it travels, so sliding a thumb up off
    // FIRE toward NITRO aims the gun rather than pressing the nitro — a
    // control you have to stay inside is not one you can use without looking,
    // and nobody looks at their thumb during a fight.
    const held = this.buttonPointer.get(e.pointerId);
    if (held) {
      if (!held.stick && !held.stick2) return;
      e.preventDefault();
      const anchor = this.buttonAnchor.get(e.pointerId) ?? e.clientY;
      if (held.stick2) {
        const ax = this.buttonAnchorX.get(e.pointerId) ?? e.clientX;
        const range2 = held.r * 1.8;
        held.knobX = clamp1((e.clientX - ax) / range2);
        held.knobY = clamp1(-(e.clientY - anchor) / range2);
        this.viewX = held.knobX;
        this.viewY = held.knobY;
        this.buttonTravel.set(e.pointerId, Math.max(
          this.buttonTravel.get(e.pointerId) ?? 0,
          Math.hypot(e.clientX - ax, e.clientY - anchor),
        ));
        return;
      }
      // Full travel in about a button and a half — roughly 60 px on a phone.
      // Short enough to reach without shifting grip, long enough that the ends
      // are not twitchy.
      const range = held.r * 1.5;
      held.knobY = clamp1(-(e.clientY - anchor) / range);
      this.gunY = held.knobY;
      return;
    }
    if (e.pointerId !== this.stickPointer) return;
    e.preventDefault();
    const L = this.layout;
    const R = L.stickR;

    let dx = e.clientX - L.stickX;
    let dy = e.clientY - L.stickY;
    let d = Math.hypot(dx, dy);

    // Sticky re-anchor: drag past full deflection and the origin follows the
    // thumb. Without this the stick pins at the screen edge — a thumb that
    // lands low in a short landscape window simply runs out of room and can
    // never reach full down or full right.
    if (d > R) {
      L.stickX += ((d - R) * dx) / d;
      L.stickY += ((d - R) * dy) / d;
      dx = e.clientX - L.stickX;
      dy = e.clientY - L.stickY;
      d = Math.hypot(dx, dy);
    }

    const nx = dx / R;
    const ny = dy / R;
    L.knobX = nx;
    L.knobY = ny;

    // Anchoring at the thumb removes the need for a real dead zone; this is
    // only enough to stop a resting thumb drifting.
    const len = d / R;
    const dead = 0.05;
    const k = len < dead ? 0 : (len - dead) / (1 - dead) / len;
    this.moveX = clamp1(nx * k);
    this.moveY = -clamp1(ny * k);
  }

  private up(e: PointerEvent) {
    const btn = this.buttonPointer.get(e.pointerId);
    if (btn) {
      btn.pressed = false;
      this.buttonPointer.delete(e.pointerId);
      if (btn.stick || btn.stick2) {
        // A press that never travelled more than a few pixels is a TAP, and on
        // the view control a tap is the reset. 10 px rather than 0 because a
        // thumb on glass always moves a little and a control that demanded
        // stillness would simply never fire.
        if (btn.stick2 && (this.buttonTravel.get(e.pointerId) ?? 0) < 10) {
          this.viewTapped = true;
        }
        this.buttonAnchor.delete(e.pointerId);
        this.buttonAnchorX.delete(e.pointerId);
        this.buttonTravel.delete(e.pointerId);
        btn.knobX = 0; btn.knobY = 0;
        if (btn.stick) this.gunY = 0;
        if (btn.stick2) { this.viewX = 0; this.viewY = 0; }
      }
      return;
    }
    if (e.pointerId === this.stickPointer) {
      this.stickPointer = null;
      try { this.el.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
      const L = this.layout;
      L.active = false;
      L.knobX = 0;
      L.knobY = 0;
      L.stickX = L.restX;
      L.stickY = L.restY;
      this.moveX = 0;
      this.moveY = 0;
    }
  }

  /** Called when the player uses a key or a gamepad — auto mode hides again. */
  noteOtherInput() {
    if (this.mode === 'auto' && this.stickPointer === null && this.buttonPointer.size === 0) {
      this.touched = false;
    }
  }

  setMode(m: TouchMode) {
    this.mode = m;
    saveTouchMode(m);
    if (m === 'off') {
      for (const b of this.layout.buttons) { b.pressed = false; b.knobX = 0; b.knobY = 0; }
      this.gunY = 0;
      this.viewX = 0;
      this.viewY = 0;
      this.moveX = 0;
      this.moveY = 0;
      this.touched = false;
    }
  }

  pressed(id: TouchButton['id']): boolean {
    if (!this.enabled) return false;
    const b = this.layout.buttons.find((q) => q.id === id);
    return !!b && b.pressed;
  }

  update(dt: number) {
    for (const b of this.layout.buttons) {
      const target = b.pressed ? 1 : 0;
      b.glow += (target - b.glow) * Math.min(1, dt * 18);
    }
  }
}
